import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import DashboardLayout from './components/DashboardLayout';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = sessionStorage.getItem('token');
  if (!token) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for token in sessionStorage
    const storedToken = sessionStorage.getItem('token');
    setToken(storedToken);
    setLoading(false);
  }, []);

  // Auto-logout on browser close/tab close
  // sessionStorage automatically clears on tab close, but we'll also clear explicitly
  useEffect(() => {
    const clearAuth = () => {
      try {
        const currentToken = sessionStorage.getItem('token');
        if (currentToken) {
          // Clear token and Firebase config from sessionStorage
          sessionStorage.removeItem('token');
          sessionStorage.removeItem(`firebaseConfig_${currentToken}`);
          // Clear any cached device data
          sessionStorage.removeItem(`devices_cache_${currentToken}`);
          sessionStorage.removeItem(`devices_cache_${currentToken}_timestamp`);
        }
      } catch (e) {
        console.warn('Error clearing auth:', e);
      }
    };

    // Handle browser/tab close - use multiple events for better compatibility
    const handleBeforeUnload = () => {
      clearAuth();
    };

    const handleUnload = () => {
      clearAuth();
    };

    const handlePageHide = (e) => {
      // Only clear if page is being unloaded (not just hidden/restored from cache)
      if (!e.persisted) {
        clearAuth();
      }
    };

    // Add all event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Listen for storage changes (when user logs in/out in another tab or same tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Handle cross-tab storage changes (sessionStorage doesn't trigger storage event, but we'll handle it)
      if (e.key === 'token' || e.key === null) {
        const storedToken = sessionStorage.getItem('token');
        setToken(storedToken);
      }
    };

    // Listen for custom login event (same-tab login)
    const handleLogin = () => {
      const storedToken = sessionStorage.getItem('token');
      setToken(storedToken);
    };

    // Note: sessionStorage doesn't trigger 'storage' event across tabs
    // But we'll keep it for localStorage compatibility if needed
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tokenUpdated', handleLogin);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenUpdated', handleLogin);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        <Routes>
          <Route 
            path="/" 
            element={token ? <Navigate to="/dashboard" replace /> : <Login />} 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/device/:deviceId" 
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            } 
          />
          {/* Catch all invalid routes (404) - always redirect to login */}
          {/* This handles: /login, /devices, /any-invalid-path, subdomain paths, etc. */}
          <Route 
            path="*" 
            element={<Navigate to="/" replace />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
